import { useCallback, useRef, useState } from "react";

import { clamp } from "lodash-es";
import { UseFormReturn } from "react-hook-form";
import { twJoin } from "tailwind-merge";
import { useEventListener, useLocalStorage } from "usehooks-ts";

import { FeatureQueryAccordion } from "src/featureCatalogue/Queries/Create/FeatureQueryAccordion";
import { FeatureQueryPreview } from "src/featureCatalogue/Queries/Create/FeatureQueryPreview";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { useFeatureAutoMapping } from "src/featureCatalogue/Queries/Create/useFeatureAutoMapping";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";

const MIN_LEFT_PERCENT = 35;
const MAX_LEFT_PERCENT = 60;
const DEFAULT_LEFT_PERCENT = 40;

type FeatureQueryFormProps = {
  form: UseFormReturn<QueryFormValues>;
  featureQueryPreview: UseFeatureQueryPreview;
  readOnly: boolean;
  hasFeatures: boolean;
  isLoading: boolean;
  isEditMode: boolean;
};

export const FeatureQueryForm: React.FC<FeatureQueryFormProps> = ({
  form,
  featureQueryPreview,
  readOnly,
  hasFeatures,
  isLoading,
  isEditMode,
}) => {
  const { handleFeatureGeneration, columnTypes, setColumnType } =
    useFeatureAutoMapping(form, isEditMode);

  const containerRef = useRef<HTMLDivElement>(null);
  const [leftPercent, setLeftPercent] = useLocalStorage<number>(
    "fq-creation-left-pane-width",
    DEFAULT_LEFT_PERCENT,
  );
  const clampedLeftPercent = clamp(
    leftPercent,
    MIN_LEFT_PERCENT,
    MAX_LEFT_PERCENT,
  );
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEventListener("mousemove", (event: MouseEvent) => {
    if (!isResizing) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const percent = ((event.clientX - rect.left) / rect.width) * 100;
    setLeftPercent(clamp(percent, MIN_LEFT_PERCENT, MAX_LEFT_PERCENT));
  });

  useEventListener("mouseup", () => {
    if (!isResizing) return;
    setIsResizing(false);
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        ref={containerRef}
        className={twJoin(
          "flex min-h-0 flex-1",
          isResizing && "cursor-col-resize select-none",
        )}
      >
        {/* Left column - scrollable accordions */}
        <div
          className="decideScrollbar compensate-scrollbar-gutter flex min-w-0 flex-col gap-y-4 overflow-y-auto border-gray-200 border-r p-6"
          style={{ width: `${clampedLeftPercent}%` }}
        >
          {/* SQL Query & Features Section */}
          <FeatureQueryAccordion
            columnTypes={columnTypes}
            featureQueryPreview={featureQueryPreview}
            handleFeatureGeneration={handleFeatureGeneration}
            hasFeatures={hasFeatures}
            isEditMode={isEditMode}
            isLoading={isLoading}
            readOnly={readOnly}
          />
        </div>

        {/* Resize handle */}
        <button
          aria-label="Resize pane"
          className={twJoin(
            "w-1 shrink-0 cursor-col-resize bg-indigo-500 opacity-0 transition-opacity duration-150 hover:opacity-100",
            isResizing && "opacity-100",
          )}
          type="button"
          onMouseDown={handleResizeStart}
        />

        {/* Right column - preview */}
        <div className="min-h-0 min-w-0 flex-1 overflow-hidden pt-3 pl-5">
          {/* Preview section */}
          <FeatureQueryPreview
            columnTypes={columnTypes}
            previewMutation={featureQueryPreview}
            onColumnTypeChange={setColumnType}
          />
        </div>
      </div>
    </div>
  );
};
